/**
 * Shared API error decoder used by every submit handler.
 *
 * Handles:
 *   • Network failures (no `error.response`)        → "Network error: …"
 *   • Plain string `detail`                         → returned as-is
 *   • Pydantic 422 array `detail`                   → flattened to "field.path: msg · …"
 *   • Object `detail`                               → JSON dump
 *   • Body without `detail` but with `message`      → uses `message`
 *   • Anything else                                 → "HTTP <status> <statusText>"
 *
 * Returns:
 *   {
 *     message:   "human readable text",
 *     status:    492 | undefined,        // HTTP status if a response came back
 *     statusTag: "[HTTP 502]" | "[no response]",
 *     toast:     "Pretty title [HTTP 502]: message"   // ready to drop into toast.error()
 *   }
 *
 * Always logs the full context to the console so the user can screenshot devtools.
 */
export function formatApiError(error, fallbackTitle = "Request failed") {
  const resp = error?.response;
  let detailMsg;

  if (!resp) {
    detailMsg = `Network error: ${error?.message || "no response from server"}. Check your connection and retry.`;
  } else if (resp.data && resp.data.detail !== undefined && resp.data.detail !== null) {
    const d = resp.data.detail;
    if (typeof d === "string") {
      detailMsg = d;
    } else if (Array.isArray(d)) {
      // Pydantic 422: [{loc: ["body","totals","foo"], msg: "field required", ...}]
      detailMsg = d
        .map((e) => `${(e.loc || []).slice(1).join(".") || "?"}: ${e.msg || JSON.stringify(e)}`)
        .join(" · ");
    } else if (typeof d === "object") {
      // Prefer a string `.message` on the object when present — most
      // backend error payloads (e.g. the PO validator's
      // `{code, message, field, value, ...}`) put the human-readable
      // sentence there. Falling straight through to `JSON.stringify`
      // is crash-safe but leaks a raw JSON blob into the toast, which
      // AC5 explicitly forbids. Feb 2026 refinement — same widening
      // helps every other consumer that surfaces this via toasts /
      // inline error banners.
      if (typeof d.message === "string" && d.message) {
        detailMsg = d.message;
      } else {
        try {
          detailMsg = JSON.stringify(d);
        } catch {
          detailMsg = String(d);
        }
      }
    } else {
      detailMsg = String(d);
    }
  } else if (resp.data && resp.data.message) {
    detailMsg = resp.data.message;
  } else if (typeof resp.data === "string" && resp.data.length > 0 && resp.data.length < 500) {
    // Sometimes upstream gateways return plain text — surface it short-form.
    detailMsg = resp.data;
  } else {
    detailMsg = `HTTP ${resp.status ?? "?"} ${resp.statusText || fallbackTitle}`;
  }

  const statusTag = resp ? `[HTTP ${resp.status}]` : "[no response]";
  const isZoho = (detailMsg || "").toLowerCase().includes("zoho");
  const title = isZoho ? "Zoho Books error" : fallbackTitle;
  const toast = `${title} ${statusTag}: ${detailMsg}`;

  // Always log full context — user can screenshot devtools console for support.
  // eslint-disable-next-line no-console
  console.error(`[${title}]`, {
    status: resp?.status,
    statusText: resp?.statusText,
    responseData: resp?.data,
    url: resp?.config?.url,
    method: resp?.config?.method,
    error,
  });

  return { message: detailMsg, status: resp?.status, statusTag, toast };
}

/**
 * Convenience wrapper for `toast.error(formatApiError(...).toast, { duration: 12000 })`.
 * Returns the formatted message in case a caller wants to use it for further logic.
 */
export function toastApiError(toastFn, error, fallbackTitle, options = {}) {
  const fmt = formatApiError(error, fallbackTitle);
  toastFn(fmt.toast, { duration: 12000, ...options });
  return fmt;
}
